import type { ContentIndex, LevelId } from '@ezishizi/content-schema';
import {
  CHALLENGE,
  effectiveLessonOrder,
  pickIslandGames,
  wordModule,
  type GameId,
  type IslandGames,
  type IslandSlot,
  type Module,
  type WordGame,
} from '@ezishizi/learning-core';
import { activityKey, type ProgressMap } from './progress.ts';

/**
 * The adventure path: every word set is an island, islands chain left to right into lessons, and
 * a lesson's last island ends with its story. Three layouts share the islands, the gate and the
 * rewards and differ only in the land stops:
 *
 * - `games` (the default): six stops that each play a minigame on the set — meet it, hear it,
 *   read it, sound it, write it, use it. The format is chosen per island from the ones that set
 *   can actually build (`set.games`, computed at build time), so no two islands look the same and
 *   none is ever short a stop. Where a slot has no playable game, it runs the plain skill module
 *   it replaced, which is why the two older layouts are kept rather than deleted.
 * - `skills` (archived): seven stops that each drill every word of the set on one skill (New
 *   words, Listen, Speak, Play, Read, Write or a Review, Pinyin).
 * - `words` (archived): one stop per word, each opening a word page with that word's games (meet,
 *   listen, find, say & use, write), then a Play round on the whole set.
 *
 * In all three, the Challenge gate on the pier tests the island's own words one try each, and
 * passing it opens the reward at the pier's end.
 */
export type PathMode = 'games' | 'skills' | 'words';

/** The layouts kept only so an existing profile's path still draws; not offered to new ones. */
export const ARCHIVED_MODES: readonly PathMode[] = ['skills', 'words'];

export type StopKind =
  | 'game'
  | 'learn'
  | 'listen'
  | 'speak'
  | 'play'
  | 'read'
  | 'write'
  | 'review'
  | 'pinyin'
  | 'word'
  | 'challenge'
  /** P3+ only (`docs/missions/spec.md` §8 decision 2): the last island of a lesson with an
   * authored practice set opens it here, in place of the Challenge. */
  | 'practice'
  | 'reward';

/** A reward is a story beat, or a game — one the island has not played, when it has one spare. */
export type RewardKind = { type: 'story'; storyId: string } | { type: 'game'; game: GameId | null };

interface StopBase {
  id: string;
  lessonId: string;
  setId: string;
  chars: string;
}

export type PathNode =
  | (StopBase & { kind: Exclude<StopKind, 'reward' | 'word' | 'game' | 'practice'> })
  /** A games-layout stop. `game` null means the slot had nothing playable on this set and the
   * stop runs the plain skill module it replaced — `island_<slot>` builds either way, so the
   * child's progress key does not depend on which. `alt`, on one stop per island, is a second
   * format to choose from: tapping the stop asks which of `game` and `alt` to play. */
  | (StopBase & { kind: 'game'; slot: IslandSlot; game: GameId | null; alt?: GameId })
  | (StopBase & { kind: 'word'; char: string; games: WordGame[] })
  /** P3+ only: the lesson's authored practice set (`Practice.id`), opened in place of the
   * island's Challenge (see `StopKind.practice`). */
  | (StopBase & { kind: 'practice'; practiceId: string })
  | (StopBase & { kind: 'reward'; reward: RewardKind });

/** The runner module each skill stop and the gate launch. A word stop opens its word page; a
 * practice stop (P3+) opens the practice runner directly (see `nodeKeys`). */
export const STOP_MODULE: Record<
  Exclude<StopKind, 'reward' | 'word' | 'game' | 'practice'>,
  Module
> = {
  learn: 'learn',
  listen: 'listen',
  speak: 'speak',
  play: 'practice',
  read: 'read',
  write: 'write',
  review: 'review',
  pinyin: 'pinyin',
  challenge: 'test',
};

/** First-try score the Challenge needs before the gate opens (70%). */
export const CHALLENGE_PASS_RATIO: number = CHALLENGE.passRatio;

export type IndexLesson = ContentIndex['lessons'][number];
export type IndexSet = IndexLesson['sets'][number];

export interface Island {
  lesson: IndexLesson;
  set: IndexSet;
  /** The land stops, the Challenge gate, the reward. */
  nodes: PathNode[];
  /** Last island of its lesson: its reward is the lesson story when there is one. */
  isLast: boolean;
}

export interface PathSection {
  lesson: IndexLesson;
  islands: Island[];
  /** Every stop of the lesson in walking order (the islands' nodes flattened). */
  nodes: PathNode[];
}

/** The skill stops in walking order. The Write slot becomes a lesson review when there is nothing to write. */
export function landStops(
  set: IndexSet,
  higherChinese: boolean,
): Exclude<StopKind, 'reward' | 'challenge' | 'word' | 'game' | 'practice'>[] {
  const canWrite = higherChinese ? set.hasHigherWrite : set.hasWrite;
  return ['learn', 'listen', 'speak', 'play', 'read', canWrite ? 'write' : 'review', 'pinyin'];
}

/**
 * The games one word gets, from the index alone (mirrors learning-core's `wordGames`, which
 * needs the bundle's entry): every word is met, heard, found and used; write targets are also
 * written.
 */
export function wordGamesFromIndex(
  set: IndexSet,
  char: string,
  higherChinese: boolean,
): WordGame[] {
  const writes = higherChinese ? set.higherWriteChars : set.writeChars;
  const games: WordGame[] = ['meet', 'listen', 'find', 'use'];
  if ([...writes].includes(char)) games.push('write');
  return games;
}

/**
 * The six game stops of one island: meet the words, hear them, read them, sound them, write them,
 * use them. The Meet comes first and the rest walk in an order that varies from island to island
 * (`WALK_ORDERS`); a stop's id and progress key name its slot, not its place in the walk.
 *
 * The format for each is chosen by `pickIslandGames` from the set's own playable list. A slot
 * with nothing playable — no listening game builds for this set, or the band has not opened one
 * yet — keeps its stop and runs the plain skill module instead. A set with nothing to write is
 * the one case that loses a stop rather than falling back, because the plain write module builds
 * nothing for it; those islands have five.
 */
export function gameStops(
  set: IndexSet,
  lessonOrder: number,
  islandIndex: number,
  higherChinese: boolean,
  levelId: LevelId = 'p1',
  recent: readonly IslandGames[] = [],
): Array<{ kind: 'game'; slot: IslandSlot; game: GameId | null; alt?: GameId }> {
  const { stops } = islandPicks(set, lessonOrder, islandIndex, higherChinese, levelId, recent);
  return stops.map(({ slot, game, alt }) => ({
    kind: 'game' as const,
    slot,
    game,
    ...(alt ? { alt } : {}),
  }));
}

/** One island's stop and reward picks. `recent` is the islands before it in the level, most
 * recent first, so neighbours do not play the same format in the same slot. */
function islandPicks(
  set: IndexSet,
  lessonOrder: number,
  islandIndex: number,
  higherChinese: boolean,
  levelId: LevelId,
  recent: readonly IslandGames[],
): IslandGames {
  return pickIslandGames(
    set.games as GameId[],
    effectiveLessonOrder(levelId, lessonOrder),
    islandIndex,
    {
      hasWrite: higherChinese ? set.hasHigherWrite : set.hasWrite,
      sizes: set.gameSizes,
      recent,
    },
  );
}

export function buildPath(
  index: ContentIndex,
  levelId: string,
  higherChinese = false,
  mode: PathMode = 'skills',
): PathSection[] {
  const level = index.levels.find((l) => l.id === levelId);
  if (!level) return [];
  const out: PathSection[] = [];
  // Counted across the whole level, not per lesson: it rotates each island's game choices, so
  // two islands side by side never walk their ladders the same way.
  let islandIndex = 0;
  // The picks of the islands walked so far, most recent first, so a slot does not repeat the
  // format (or the reward) of the island before it.
  let recent: IslandGames[] = [];
  for (const id of level.lessonIds) {
    const lesson = index.lessons.find((l) => l.id === id);
    if (!lesson?.authored) continue;
    const islands: Island[] = [];
    lesson.sets.forEach((set, i) => {
      const isLast = i === lesson.sets.length - 1;
      const base = { lessonId: id, setId: set.id, chars: set.chars };
      // One pick per island, shared by its stops and its reward.
      const picks =
        mode === 'games'
          ? islandPicks(set, lesson.order, islandIndex++, higherChinese, level.id, recent)
          : null;
      if (picks) recent = [picks, ...recent].slice(0, 2);
      const nodes: PathNode[] = picks
        ? picks.stops.map(({ slot, game, alt }): PathNode => ({
            ...base,
            kind: 'game',
            slot,
            game,
            ...(alt ? { alt } : {}),
            id: `${set.id}:${slot}`,
          }))
        : mode === 'words'
          ? [
              ...[...set.chars].map((char): PathNode => ({
                ...base,
                kind: 'word',
                char,
                games: wordGamesFromIndex(set, char, higherChinese),
                id: `${set.id}:word:${char}`,
              })),
              { ...base, kind: 'play', id: `${set.id}:play` },
            ]
          : landStops(set, higherChinese).map((kind) => ({
              ...base,
              kind,
              id: `${set.id}:${kind}`,
            }));
      // P3+ decision (spec §8.2): the practice set is the island's main event, where the
      // Challenge sits at P1–P2 — so the *last* island of a lesson with an authored practice set
      // opens it there instead. Earlier islands of the same lesson (its short new-character
      // stops) keep their own Challenge, testing that island's own set as before.
      if (isLast && lesson.practiceId)
        nodes.push({
          ...base,
          kind: 'practice',
          practiceId: lesson.practiceId,
          id: `${set.id}:practice`,
        });
      else nodes.push({ ...base, kind: 'challenge', id: `${set.id}:challenge` });
      const storyId = isLast ? lesson.storyIds[0] : undefined;
      const spare = picks?.reward ?? null;
      nodes.push({
        ...base,
        kind: 'reward',
        id: `${set.id}:reward`,
        reward: storyId ? { type: 'story', storyId } : { type: 'game', game: spare },
      });
      islands.push({ lesson, set, nodes, isLast });
    });
    out.push({ lesson, islands, nodes: islands.flatMap((isl) => isl.nodes) });
  }
  return out;
}

/**
 * The progress keys a node is marked done by (the sessions the runner records for it). A word
 * stop has one per game; every other stop has exactly one.
 */
export function nodeKeys(n: PathNode): string[] {
  if (n.kind === 'reward')
    return [
      n.reward.type === 'story'
        ? activityKey(n.lessonId, null, 'story')
        : activityKey(n.lessonId, n.setId, 'game'),
    ];
  if (n.kind === 'word')
    return n.games.map((g) => activityKey(n.lessonId, n.setId, wordModule(g), n.char));
  // Lesson-scoped, not set-scoped (a Practice attaches to a lesson, not a set — spec §1.4): the
  // same key as `story`'s reward node, so it survives which island happens to be last.
  if (n.kind === 'practice') return [activityKey(n.lessonId, null, 'reading_practice')];
  // A game stop is keyed by its slot, not by the format: the format can change (a new lesson
  // band opens one, content adds another) and a stop the child finished must stay finished.
  if (n.kind === 'game') return [activityKey(n.lessonId, n.setId, `island_${n.slot}`)];
  return [activityKey(n.lessonId, n.setId, STOP_MODULE[n.kind])];
}

/** The progress key of a single-session node; for a word stop, its first game's. */
export function nodeKey(n: PathNode): string {
  return nodeKeys(n)[0]!;
}

/** Done means finished (every game, for a word stop), except the gate, which has to be passed. */
export function nodeDone(n: PathNode, progress: ProgressMap): boolean {
  if (n.kind === 'challenge') {
    const p = progress.get(nodeKey(n));
    return !!p && (p.bestRatio ?? 0) >= CHALLENGE_PASS_RATIO;
  }
  return nodeKeys(n).every((k) => progress.has(k));
}

/** Best stars for a node: the weakest game's, for a word stop, so three stars mean the whole word. */
export function nodeStars(n: PathNode, progress: ProgressMap): number {
  const keys = nodeKeys(n);
  if (n.kind !== 'word') return progress.get(keys[0]!)?.bestStars ?? 0;
  return Math.min(...keys.map((k) => progress.get(k)?.bestStars ?? 0));
}

/** A Challenge that has been tried but not passed yet: the stop shows "try again". */
export function challengeAttempted(n: PathNode, progress: ProgressMap): boolean {
  return n.kind === 'challenge' && progress.has(nodeKey(n)) && !nodeDone(n, progress);
}

export type NodeStatus = 'done' | 'current' | 'open' | 'locked';

/**
 * The order of the lesson the child is unlocked up to (`profile.currentLessonId`): that lesson
 * and every earlier one are playable. A lesson id the index doesn't know unlocks lesson 1 only.
 */
export function unlockedOrder(index: ContentIndex, lessonId: string): number {
  return index.lessons.find((l) => l.id === lessonId)?.order ?? 1;
}

/**
 * A lesson is unlocked when it is at or before the unlock frontier, or when the child has already
 * finished something on it (a parent lowering the frontier never hides finished work).
 */
export function lessonUnlocked(
  section: PathSection,
  progress: ProgressMap,
  unlockedTo: number,
): boolean {
  return section.lesson.order <= unlockedTo || section.nodes.some((n) => nodeDone(n, progress));
}

/**
 * The lesson the child should be moved on to, when the one they are on (`currentLessonId`) has
 * every one of its stops done — `null` when it isn't finished yet, when it is the trail's last
 * lesson, or when `currentLessonId` names no section here. Pure decision only: Path.tsx's own
 * effect is what calls it and persists the move (`lib/pathNav.ts`'s `persistLessonAdvance`), and
 * the result screen's "Next" button (Result.tsx) calls it too, so a lesson's last stop can offer
 * the next lesson's first one before Path.tsx's effect has had a chance to run.
 */
export function lessonToAdvanceTo(
  sections: PathSection[],
  progress: ProgressMap,
  currentLessonId: string,
): string | null {
  const at = sections.findIndex((s) => s.lesson.id === currentLessonId);
  const section = sections[at];
  const next = sections[at + 1];
  if (!section || !next) return null;
  if (!section.nodes.every((n) => nodeDone(n, progress))) return null;
  return next.lesson.id;
}

/**
 * Within the unlocked lessons the path is open: any stop can be played at any time, in any order.
 * The one exception is an island's reward, which stays locked until every stop before it on that
 * island is done (the Challenge counts as done only when passed). Every stop of a lesson beyond
 * the unlock frontier is locked. Exactly one unfinished stop is "current": the earliest one on
 * the whole trail, so a child unlocked up to lesson 10 who has finished lessons 1 to 5 finds
 * Shizi standing on lesson 6, not on lesson 10.
 *
 * `after` is the progress key of the stop the child has just come back from. The path is open,
 * so a child can skip ahead to lesson 5 with lesson 1 unfinished; coming back from a lesson 5
 * stop must not throw them back to lesson 1. With it, current is the first unfinished stop at or
 * after that one (the same stop again if they left it unfinished), falling back to the earliest
 * when there is none further on.
 */
export function pathStatuses(
  sections: PathSection[],
  progress: ProgressMap,
  unlockedTo: number,
  after: string | null = null,
): Map<string, NodeStatus> {
  const statuses = new Map<string, NodeStatus>();
  let current: string | null = null;
  // Set once the trail reaches the stop named by `after`; the first open stop from there wins.
  let reached = false;
  let currentAfter: string | null = null;
  for (const section of sections) {
    if (!lessonUnlocked(section, progress, unlockedTo)) {
      for (const n of section.nodes) statuses.set(n.id, 'locked');
      continue;
    }
    for (const island of section.islands) {
      let allBefore = true;
      for (const n of island.nodes) {
        const done = nodeDone(n, progress);
        const status: NodeStatus = done
          ? 'done'
          : n.kind === 'reward' && !allBefore
            ? 'locked'
            : 'open';
        statuses.set(n.id, status);
        if (after && !reached && nodeKeys(n).includes(after)) reached = true;
        if (status === 'open') {
          current ??= n.id;
          if (reached) currentAfter ??= n.id;
        }
        if (!done) allBefore = false;
      }
    }
  }
  current = currentAfter ?? current;
  if (current) statuses.set(current, 'current');
  return statuses;
}

/** Id of the stop the child should play next, if any. */
export function currentNode(statuses: Map<string, NodeStatus>): string | null {
  for (const [id, s] of statuses) if (s === 'current') return id;
  return null;
}

/**
 * The stop immediately after the one keyed by `after`, in walking order (a lesson's islands
 * flattened, and the lessons themselves in sequence) — not the earliest unfinished stop, which is
 * what `currentNode` gives. Used by the result screen's "Next" button, so a child who skipped
 * ahead and just finished that stop is offered the one right after it, wherever the trail's own
 * "current" marker sits. `null` when `after` isn't found (an unknown key) or names the trail's
 * last stop.
 */
export function nextNodeAfter(sections: PathSection[], after: string): PathNode | null {
  const all = sections.flatMap((s) => s.nodes);
  const i = all.findIndex((n) => nodeKeys(n).includes(after));
  if (i < 0) return null;
  return all[i + 1] ?? null;
}

/** An island is locked when every stop on it is; current when it holds the current stop. */
export function islandStatus(island: Island, statuses: Map<string, NodeStatus>): NodeStatus {
  const st = island.nodes.map((n) => statuses.get(n.id) ?? 'locked');
  if (st.includes('current')) return 'current';
  if (st.every((s) => s === 'done')) return 'done';
  if (st.every((s) => s === 'locked')) return 'locked';
  return 'open';
}
